import { LandingPageOption, PreferenceKeys } from '@app/models/enums'
import { ApplicationState } from '@app/store'
import { selectPreference } from '@app/store/modules/account'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IPreferencesEditState {
  readonly error?: string
  readonly isLoading: boolean
  readonly isOpen: boolean
  readonly isSaving: boolean
  readonly landingPage: LandingPageOption
}

const PreferencesEditStateRecord = Record({
  error: undefined,
  isLoading: false,
  isOpen: false,
  isSaving: false,
  landingPage: LandingPageOption.LastPage,
})

export class PreferencesEditState extends PreferencesEditStateRecord
  implements IPreferencesEditState {
  public static fromJS(initialState?: Partial<IPreferencesEditState>) {
    return new PreferencesEditState(initialState)
  }

  public readonly error?: string
  public readonly isLoading: boolean
  public readonly isOpen: boolean
  public readonly isSaving: boolean
  public readonly landingPage: LandingPageOption

  constructor(props?: Partial<PreferencesEditState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------
export enum ActionTypes {
  ClosePreferencesModal = 'PREFERENCES::CLOSE_PREFERENCES_MODAL',
  LoadPreferences = 'PREFERENCES::LOAD_PREFERENCES',
  LoadPreferencesFailure = 'PREFERENCES::LOAD_PREFERENCES_FAILURE',
  LoadPreferencesRequest = 'PREFERENCES::LOAD_PREFERENCES_REQUEST',
  LoadPreferencesSuccess = 'PREFERENCES::LOAD_PREFERENCES_SUCCESS',
  ResetState = 'PREFERENCES::RESET_STATE',
  SetLandingPage = 'PREFERENCES::SET_LANDING_PAGE',
  ShowPreferencesModal = 'PREFERENCES::SHOW_PREFERENCES_MODAL',
  UpdatePreferences = 'PREFERENCES::UPDATE_PREFERENCES',
  UpdatePreferencesFailure = 'PREFERENCES::UPDATE_PREFERENCES_FAILURE',
  UpdatePreferencesRequest = 'PREFERENCES::UPDATE_PREFERENCES_REQUEST',
  UpdatePreferencesSuccess = 'PREFERENCES::UPDATE_PREFERENCES_SUCCESS',
}

interface IClosePreferencesModalAction {
  type: ActionTypes.ClosePreferencesModal
}

interface ILoadPreferencesAction {
  type: ActionTypes.LoadPreferences
}

interface ILoadPreferencesFailureAction {
  error: string
  type: ActionTypes.LoadPreferencesFailure
}

interface ILoadPreferencesRequestAction {
  type: ActionTypes.LoadPreferencesRequest
}

interface ILoadPreferencesSuccessAction {
  landingPage: LandingPageOption
  type: ActionTypes.LoadPreferencesSuccess
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISetLandingPageAction {
  landingPage: string
  type: ActionTypes.SetLandingPage
}

interface IShowPreferencesModalAction {
  type: ActionTypes.ShowPreferencesModal
}

interface IUpdatePreferencesAction {
  landingPage: LandingPageOption
  type: ActionTypes.UpdatePreferences
}

interface IUpdatePreferencesFailureAction {
  error: string
  type: ActionTypes.UpdatePreferencesFailure
}

interface IUpdatePreferencesRequestAction {
  type: ActionTypes.UpdatePreferencesRequest
}

export interface IUpdatePreferencesSuccessAction {
  landingPage: LandingPageOption
  type: ActionTypes.UpdatePreferencesSuccess
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IClosePreferencesModalAction
  | ILoadPreferencesFailureAction
  | ILoadPreferencesRequestAction
  | ILoadPreferencesSuccessAction
  | IResetStateAction
  | ISetLandingPageAction
  | IShowPreferencesModalAction
  | IUpdatePreferencesFailureAction
  | IUpdatePreferencesRequestAction
  | IUpdatePreferencesSuccessAction

// -------------------------
// ACTION CREATORS
// -------------------------
function closePreferencesModal(): IClosePreferencesModalAction {
  return { type: ActionTypes.ClosePreferencesModal }
}

function loadPreferences(): ILoadPreferencesAction {
  return {
    type: ActionTypes.LoadPreferences,
  }
}

function loadPreferencesFailure(error: string): ILoadPreferencesFailureAction {
  return {
    error,
    type: ActionTypes.LoadPreferencesFailure,
  }
}

function loadPreferencesRequest(): ILoadPreferencesRequestAction {
  return { type: ActionTypes.LoadPreferencesRequest }
}

function loadPreferencesSuccess(
  landingPage: LandingPageOption,
): ILoadPreferencesSuccessAction {
  return {
    landingPage,
    type: ActionTypes.LoadPreferencesSuccess,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function setLandingPage(landingPage: string): ISetLandingPageAction {
  return { type: ActionTypes.SetLandingPage, landingPage }
}

function showPreferencesModal(): IShowPreferencesModalAction {
  return { type: ActionTypes.ShowPreferencesModal }
}

function updatePreferences(
  landingPage: LandingPageOption,
): IUpdatePreferencesAction {
  return {
    landingPage,
    type: ActionTypes.UpdatePreferences,
  }
}

function updatePreferencesFailure(
  error: string,
): IUpdatePreferencesFailureAction {
  return {
    error,
    type: ActionTypes.UpdatePreferencesFailure,
  }
}

function updatePreferencesRequest(): IUpdatePreferencesRequestAction {
  return { type: ActionTypes.UpdatePreferencesRequest }
}

function updatePreferencesSuccess(
  landingPage: LandingPageOption,
): IUpdatePreferencesSuccessAction {
  return {
    landingPage,
    type: ActionTypes.UpdatePreferencesSuccess,
  }
}

export const actionCreators = {
  closePreferencesModal,
  loadPreferences,
  resetState,
  setLandingPage,
  updatePreferences,
}

export const preferencesEditActionCreators = {
  showPreferencesModal,
}

// -------------------------
// REDUCER
// -------------------------
export const reducer: Reducer<PreferencesEditState> = (
  state: PreferencesEditState = new PreferencesEditState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction

  switch (action.type) {
    case ActionTypes.ClosePreferencesModal:
      return state.set('isOpen', false) as PreferencesEditState

    case ActionTypes.LoadPreferencesFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as PreferencesEditState

    case ActionTypes.LoadPreferencesRequest:
      return state.set('isLoading', true) as PreferencesEditState

    case ActionTypes.LoadPreferencesSuccess:
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('landingPage', action.landingPage) as PreferencesEditState

    case ActionTypes.ResetState:
      return new PreferencesEditState()

    case ActionTypes.SetLandingPage:
      return state.set(
        'landingPage',
        action.landingPage,
      ) as PreferencesEditState

    case ActionTypes.ShowPreferencesModal:
      return state.set('isOpen', true) as PreferencesEditState

    case ActionTypes.UpdatePreferencesFailure:
      return state
        .set('error', action.error)
        .set('isSaving', false) as PreferencesEditState

    case ActionTypes.UpdatePreferencesRequest:
      return state.set('isSaving', true) as PreferencesEditState

    case ActionTypes.UpdatePreferencesSuccess:
      return state
        .set('isOpen', false)
        .set('isSaving', false) as PreferencesEditState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadPreferencesSaga() {
  try {
    yield put(loadPreferencesRequest())

    const landingPage = yield select(
      selectPreference(PreferenceKeys.LandingPage),
    )

    yield put(
      loadPreferencesSuccess(landingPage || LandingPageOption.LastPage),
    )
  } catch (err) {
    yield put(loadPreferencesFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* updatePreferencesSaga(action: IUpdatePreferencesAction) {
  try {
    yield put(updatePreferencesRequest())

    const reqOpts = {
      body: JSON.stringify({
        key: PreferenceKeys.LandingPage,
        value: action.landingPage,
      }),
      method: RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(
      '/api/Users/Preferences',
      reqOpts,
    )

    if (result.success === true) {
      yield put(updatePreferencesSuccess(action.landingPage))
      yield put(successToast('Success!', 'Your preferences have been updated'))
    } else {
      yield put(updatePreferencesFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(updatePreferencesFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadPreferences() {
  yield takeLatest(ActionTypes.LoadPreferences, loadPreferencesSaga)
}

function* watchUpdatePreferences() {
  yield takeLatest(ActionTypes.UpdatePreferences, updatePreferencesSaga)
}

export function* preferencesEditRootSaga() {
  yield fork(watchLoadPreferences)
  yield fork(watchUpdatePreferences)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) =>
  state.preferencesEdit

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectIsOpen = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isOpen,
  )

export const selectIsSaving = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isSaving,
  )

export const selectLandingPage = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.landingPage,
  )
